import { useEffect, useState } from 'react';
import { RefreshCw, TrendingUp, CheckCircle, XCircle, BarChart2 } from 'lucide-react';
import { fetchAnalyticsSummary } from '../../lib/adminQueries';
import type { AnalyticsSummary } from '../../types/admin';

type Period = 7 | 30 | 90;

function StatCard({
  label,
  value,
  sub,
  color = 'blue',
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: 'blue' | 'green' | 'red' | 'gray';
}) {
  const bg = { blue: 'bg-blue-50', green: 'bg-green-50', red: 'bg-red-50', gray: 'bg-gray-50' }[color];
  const text = { blue: 'text-blue-700', green: 'text-green-700', red: 'text-red-700', gray: 'text-gray-700' }[color];
  return (
    <div className={`${bg} rounded-lg p-4 border border-gray-100`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-3xl font-bold ${text}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function Bar({ label, count, max }: { label: string; count: number; max: number }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-600 w-48 truncate flex-shrink-0" title={label}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
        <div
          className="h-4 bg-blue-500 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-700 w-6 text-right">{count}</span>
    </div>
  );
}

const BLOCKER_LABELS: Record<string, string> = {
  customer_name_required: 'Customer name missing',
  customer_address_required: 'Property address missing',
  customer_zip_required: 'ZIP code missing',
  customer_zip_invalid: 'ZIP code malformed',
  prepared_by_required: 'Prepared By missing',
  preparer_email_required: 'Preparer email missing',
  preparer_email_invalid: 'Preparer email invalid',
  service_rating_required: 'Service rating missing',
  service_voltage_required: 'Service voltage missing',
  utility_provider_required: 'Utility provider missing',
  ami_data_required: 'No AMI data',
  ami_insufficient_range: 'AMI < 30 days',
  equipment_rating_required: 'Equipment missing rating',
  equipment_type_required: 'Equipment missing type',
  equipment_source_required: 'Equipment missing source',
  equipment_override_reason_required: 'Concurrency override unexplained',
  traditional_ineligible: 'Traditional calc out of scope',
  dwelling_floor_area_required: 'Floor area missing',
  existing_load_rating_required: 'Inventory row missing rating',
  inventory_attested_required: 'Inventory not attested',
};

const WARNING_LABELS: Record<string, string> = {
  ami_unit_conversion: 'Unit conversion applied',
  ami_data_gaps: 'Data gaps / irregular intervals',
  ami_dst_artifacts: 'DST artifacts',
  ami_quality_notes_present: 'Quality notes present',
  ami_low_point_count: 'Low AMI point count',
  dwelling_defaults_in_use: 'Inventory defaults in use',
  floor_area_band: 'Floor area outside typical range',
  small_appliance_below_minimum: 'Small-appliance circuits below minimum',
  laundry_below_minimum: 'No laundry circuit',
  inventory_empty: 'Inventory empty',
  inventory_missing_categories: 'Inventory missing a usual category',
  dryer_laundry_flag_implausible: 'Dryer laundry-circuit flag implausible',
  demand_parameters_stated: 'Demand parameters stated by preparer',
};

export default function AdminAnalytics() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>(30);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await fetchAnalyticsSummary());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading analytics…</div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">{error}</div>;
  if (!summary) return null;

  const created = period === 7 ? summary.projectsCreated7d : period === 30 ? summary.projectsCreated30d : summary.projectsCreated90d;
  const completed = period === 7 ? summary.projectsCompleted7d : period === 30 ? summary.projectsCompleted30d : summary.projectsCompleted90d;
  const maxBlocker = Math.max(1, ...summary.topBlockers.map(b => b.count));
  const maxWarning = Math.max(1, ...summary.topWarnings.map(w => w.count));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Analytics</h2>
        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm">
            {([7, 30, 90] as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 ${period === p ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {p}d
              </button>
            ))}
          </div>
          <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Projects" value={summary.totalProjects} color="blue" />
        <StatCard label="Completed" value={summary.completedProjects} sub={`${summary.completionRate}% completion rate`} color="green" />
        <StatCard label="PASS" value={summary.passCount} color="green" />
        <StatCard label="FAIL" value={summary.failCount} color="red" />
      </div>

      {/* Period stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <h3 className="font-medium text-gray-900 text-sm">Activity (last {period} days)</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Projects created</span>
              <span className="font-semibold text-gray-900">{created}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Projects completed</span>
              <span className="font-semibold text-gray-900">{completed}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Warnings acknowledged</span>
              <span className="font-semibold text-gray-900">{summary.warningAcknowledgedCount}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart2 className="w-4 h-4 text-purple-500" />
            <h3 className="font-medium text-gray-900 text-sm">PASS / FAIL Breakdown</h3>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">PASS</span>
              <span className="font-semibold">{summary.passCount}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-gray-600">FAIL</span>
              <span className="font-semibold">{summary.failCount}</span>
            </div>
          </div>
          {(summary.passCount + summary.failCount) > 0 && (
            <div className="mt-3 h-4 rounded-full overflow-hidden bg-red-100 flex">
              <div
                className="h-4 bg-green-400 transition-all"
                style={{ width: `${Math.round(summary.passCount / (summary.passCount + summary.failCount) * 100)}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Method mix — the strategy number: how much of the book is measured */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="method-mix">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 className="w-4 h-4 text-blue-500" />
          <h3 className="font-medium text-gray-900 text-sm">Projects by method</h3>
        </div>
        <div className="space-y-2">
          <Bar label="Measured demand" count={summary.methodMix.measured} max={Math.max(1, summary.totalProjects)} />
          <Bar label="Traditional calculation" count={summary.methodMix.traditional} max={Math.max(1, summary.totalProjects)} />
          <Bar label="No existing load yet" count={summary.methodMix.unset} max={Math.max(1, summary.totalProjects)} />
        </div>
        <p className="text-xs text-gray-400 mt-3">
          {summary.methodMix.measured + summary.methodMix.traditional > 0
            ? `${Math.round((summary.methodMix.measured / (summary.methodMix.measured + summary.methodMix.traditional)) * 100)}% of projects with an existing load use measured demand. `
            : ''}
          Completed: {summary.methodMix.completedMeasured} measured, {summary.methodMix.completedTraditional} traditional. A stored report basis counts first; otherwise a dwelling profile reads as traditional and a utility peak as measured.
        </p>
      </div>

      {/* Top blockers */}
      {summary.topBlockers.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 text-sm mb-4">Top Completion Blockers</h3>
          <div className="space-y-2">
            {summary.topBlockers.map(b => (
              <Bar key={b.id} label={BLOCKER_LABELS[b.id] ?? b.id} count={b.count} max={maxBlocker} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Counts reflect blockers present at the time of project completion validation.
          </p>
        </div>
      )}

      {/* Top warnings */}
      {summary.topWarnings.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 text-sm mb-4">Top Warnings (Acknowledged)</h3>
          <div className="space-y-2">
            {summary.topWarnings.map(w => (
              <Bar key={w.id} label={WARNING_LABELS[w.id] ?? w.id} count={w.count} max={maxWarning} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Per TrueLoad's defensibility principles, warnings are disclosed — not rejected. These are quality items acknowledged by preparers.
          </p>
        </div>
      )}

      {summary.topBlockers.length === 0 && summary.topWarnings.length === 0 && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
          No validation snapshot data yet. Blocker and warning trends will appear here as projects are completed.
        </div>
      )}
    </div>
  );
}
